// «النبضة» — سؤالٌ واحد كل عشر ثوانٍ بدل خمسة أسئلة منفصلة (قرار المالك 27/09/2026: «ايه ال يخلى البرنامج
// سريع دايما مع الشباب»).
//
// كان الجرس يسأل كل دقيقة، والإعلان كل عشرين ثانية، وشات الموقع كل عشر، وشات الفريق كل خمس عشرة، وطلبات
// الموقع كل دقيقة — ~15 طلبًا في الدقيقة من كل جهاز، كلٌّ يوقظ الشبكة ويستهلك بطارية. الآن `/pulse` وحدها
// كل عشر ثوانٍ، **والصفحة ظاهرة فقط** (المخفيّة لا يراها أحد فلا تسأل)، وفورًا حين تعود إلى الواجهة.
//
// والشاشات لا تُعيد تحميل قوائمها الكاملة إلا حين يتغيّر **مفتاحُها** في النبضة (عدد + آخر وقت) — فالجرس
// يقرأ إشعاراته حين يصل إشعار، لا كل دقيقة على الفاضي. وبلا مزوّد (كالاختبارات القديمة) تعمل الشاشات كما
// كانت بأسئلتها المنفصلة.
import { createContext, useContext, useEffect, useRef, useMemo, useState, type ReactNode } from 'react';
import { get, type Pulse } from './api.ts';
import { useOptionalSession } from '../auth/session.tsx';

const PULSE_TIMEOUT_MS = 8_000;
/**
 * والصفحة مخفيّة: نبضةٌ كل دقيقة **بالوقت لا بعدّ النبضات** (قرار المالك 01/10/2026: «وصل مرة وبعدها ما وصلش»):
 * المتصفّح يبطّئ مؤقّتات التبويب المخفيّ إلى مرّةٍ في الدقيقة، فعدُّ ستّ نبضاتٍ صار ستّ دقائق — فتتأخّر الإشعارات
 * ويخرج البياع من «فاتح البرنامج» بعد 5 دقائق.
 */
const HIDDEN_MS = 55_000;
const PULSE_MS = 10_000;

export interface PulseState {
  /** آخرُ نبضةٍ نجحت؟ حين تفشل (خادمٌ قديم أثناء النشر، 502) تعود الشاشات إلى أسئلتها القديمة حتى تعود (مراجعة 27/09/2026) */
  healthy: boolean;
  data: Pulse | null;
  /** مفاتيح التغيّر: تتبدّل حين يتبدّل ما تعنيه — فالشاشة تُعيد التحميل عندها لا كل نبضة */
  key: { notifications: string; chats: string; team: string; webLeads: string };
}
const Ctx = createContext<PulseState | null>(null);

const keysOf = (p: Pulse | null): PulseState['key'] => p ? {
  notifications: `${p.notifications.unread}|${p.notifications.latestAt ?? ''}`,
  chats: `${p.chats.waiting}|${p.chats.active}|${p.chats.lastVisitorSeq}`,
  team: `${p.team.unread}|${p.team.lastSeq}`,
  webLeads: `${p.webLeads.open}|${p.webLeads.queued}|${p.webLeads.latestAt ?? ''}`,
} : { notifications: '', chats: '', team: '', webLeads: '' };

export function PulseProvider({ children }: { children: ReactNode }) {
  const me = useOptionalSession()?.me ?? null;
  const enabled = me !== null && !me.mustChangePassword;
  const [data, setData] = useState<Pulse | null>(null);
  const [key, setKey] = useState<PulseState['key']>(keysOf(null));
  const [healthy, setHealthy] = useState(true);
  const inFlight = useRef(false);
  const seq = useRef(0);
  const lastRun = useRef(0);

  useEffect(() => {
    if (!enabled) { setData(null); setKey(keysOf(null)); return; }
    let alive = true;
    const tick = async () => {
      if (inFlight.current) return;
      // والصفحة مخفيّة (تبويبٌ آخر، أو التطبيق في الخلفية): نبضةٌ كل دقيقة لا كل عشر ثوانٍ — الصمتُ التامّ كان يُخرج البياع
      // من «فاتح البرنامج» بعد 5 دقائق فتفوته طلباتُ الموقع بالدور ولا يسمع الجرس (مراجعة 27/09/2026)
      if (document.visibilityState === 'hidden' && Date.now() - lastRun.current < HIDDEN_MS) return;
      lastRun.current = Date.now();
      inFlight.current = true;
      const my = ++seq.current;
      try {
        // طلبٌ معلّق (تبديل شبكة على الهاتف) كان يحبس النبضة دقائق: بعد 8 ثوانٍ يُعدّ فاشلًا ويُتجاهل ردُّه المتأخّر
        const p = await Promise.race([get<Pulse>('/pulse'),
          new Promise<never>((_, rej) => setTimeout(() => rej(new Error('pulse-timeout')), PULSE_TIMEOUT_MS))]);
        if (!alive || my !== seq.current) return;
        setHealthy(true);
        // **لا إعادة رسمٍ بلا جديد** (المراجعة الشاملة 06/10/2026، المالك: «موافق طبعا»): كائنٌ جديد كل نبضة كان يعيد رسم الهيكل
        // والشاشة المفتوحة كل 10 ثوانٍ ولو لم يتغيّر رقم — فتُقارَن النبضة بما قبلها (بلا ساعة الخادم) ولا تُبدَّل إلّا إن اختلفت
        setData(prev => {
          if (!prev) return p;
          const strip = ({ serverNow: _s, ...rest }: Pulse) => rest;
          return JSON.stringify(strip(prev)) === JSON.stringify(strip(p)) ? prev : p;
        });
        const k = keysOf(p);
        setKey(prev => (prev.notifications === k.notifications && prev.chats === k.chats && prev.team === k.team && prev.webLeads === k.webLeads) ? prev : k);
      } catch { if (alive && my === seq.current) setHealthy(false); /* شبكةٌ مقطوعة أو خادمٌ يُعاد تشغيله: الشاشات تعود لأسئلتها حتى تعود النبضة */ }
      finally { if (my === seq.current) inFlight.current = false; }
    };
    void tick();
    const id = setInterval(() => { void tick(); }, PULSE_MS);
    const onWake = () => { if (document.visibilityState === 'visible') void tick(); };
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    return () => { alive = false; clearInterval(id); document.removeEventListener('visibilitychange', onWake); window.removeEventListener('focus', onWake); };
  }, [enabled]);

  // قيمةٌ ثابتة ما لم يتغيّر شيء: كائنٌ جديد كل نبضة كان يعيد رسم الهيكل والشاشة المفتوحة كلها كل 10 ثوانٍ
  const value = useMemo<PulseState>(() => ({ data, key, healthy }), [data, key, healthy]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** النبضة إن وُجد مزوّدها، وإلّا `null` — فتعمل الشاشة بسؤالها القديم. */
export const usePulse = (): PulseState | null => useContext(Ctx);
